import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileText, Download, Clock3, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const EUR_DATE = (iso) => new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function DailyReports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [genDay, setGenDay] = useState(todayISO());
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/daily-reports");
      setReports(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const generateNow = async () => {
    setGenerating(true);
    try {
      await api.post(`/daily-reports/${genDay}/generate`);
      toast.success(`Report del ${genDay} generato`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setGenerating(false); }
  };

  const download = async (day) => {
    try {
      const res = await api.get(`/daily-reports/${day}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `report-${day}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <section className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm" data-testid="daily-reports-widget">
      <div className="flex items-center gap-2 mb-4">
        <FileText className="w-5 h-5 text-rose-600" />
        <h2 className="font-display font-bold text-slate-900">Report Fine Giornata</h2>
        <span className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-widest bg-slate-100 text-slate-500 border border-slate-200 rounded-full px-2 py-0.5">
          <Clock3 className="w-3 h-3" /> AUTO ALLE 20:00
        </span>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Ogni sera alle 20:00 l'app genera un PDF riassuntivo con lavori chiusi, ore lavorate, saldi incassati e rifornimenti.
        Puoi anche generarne uno a mano per oggi o per date passate.
      </p>

      {/* Manual trigger */}
      <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl p-3 mb-4">
        <Input
          type="date"
          value={genDay}
          onChange={(e) => setGenDay(e.target.value)}
          className="w-44 bg-white"
          data-testid="daily-report-day-input"
          max={todayISO()}
        />
        <Button
          onClick={generateNow}
          disabled={generating}
          className="bg-rose-600 hover:bg-rose-700 text-white"
          data-testid="daily-report-generate-button"
        >
          {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Genera adesso
        </Button>
      </div>

      {/* List */}
      <div className="divide-y divide-slate-100">
        {loading ? (
          <div className="text-center text-slate-400 py-6"><Loader2 className="w-5 h-5 inline animate-spin" /></div>
        ) : reports.length === 0 ? (
          <div className="text-sm text-slate-400 italic py-4 text-center">Nessun report ancora generato.</div>
        ) : (
          reports.map((r) => (
            <div key={r.date} className="py-3 flex items-center gap-3" data-testid={`report-row-${r.date}`}>
              <div className="w-14 text-center">
                <div className="font-display font-extrabold text-slate-900">
                  {new Date(r.date).toLocaleDateString("it-IT", { day: "2-digit" })}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">
                  {new Date(r.date).toLocaleDateString("it-IT", { month: "short" })}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900">Report del {new Date(r.date).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}</div>
                <div className="text-xs text-slate-400 font-mono">Generato il {EUR_DATE(r.created_at)} · {Math.round((r.size || 0) / 1024)} KB</div>
              </div>
              <Button variant="outline" size="sm" onClick={() => download(r.date)} data-testid={`report-download-${r.date}`}>
                <Download className="w-4 h-4 mr-2" /> Scarica PDF
              </Button>
            </div>
          ))
        )}
      </div>
    </section>
  );
}
